import React, { useState, useMemo } from 'react';
import { InputAdornment, IconButton, makeStyles, Theme, createStyles } from '@material-ui/core';
import Cancel from '@material-ui/icons/Cancel';
import { TagDefinition } from '../../../../models';
import { BaseInput } from 'src/components/ui/form/BaseInput';
import { Card } from 'src/components/ui/card';
import { Text } from 'src/components/ui/typography';

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    wrapper: {
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    listContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    headerContainer: {
      backgroundColor: '#e5e5e5',
      padding: '8px 16px',
      flexShrink: 0,
    },
    accordionContainer: {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      overflowY: 'auto',
      padding: '0 8px 8px',
      display: 'flex',
      flexDirection: 'column',
      '& .MuiCollapse-entered': {
        minHeight: 'auto !important',
      },
    },
    textFieldContainer: {
      padding: '8px',
      flexShrink: 0,
    },
  })
);

export interface FilterableTagListProps {
  tags: TagDefinition[];
  renderTags: (filteredTags: TagDefinition[]) => React.ReactNode;
  headerTitle?: string;
  customClasses?: {
    container?: string;
    headerContainer?: string;
  };
  filterCallback?: (tag: TagDefinition, filterText: string) => boolean;
  tabs?: React.ReactNode;
}

export const FilterableTagList: React.FC<FilterableTagListProps> = ({
  tags,
  renderTags,
  headerTitle,
  filterCallback,
  tabs,
}) => {
  const classes = useStyles();
  const [filterText, setFilterText] = useState<string | undefined>(undefined);

  const filteredTags = useMemo(() => {
    if (!filterText) return tags;

    return tags.filter((tag) => {
      if (filterCallback) {
        return filterCallback(tag, filterText);
      }
      return tag.label.toLowerCase().includes(filterText.toLowerCase());
    });
  }, [tags, filterText, filterCallback]);

  const displayTitle = headerTitle || `Available: ${tags.length}`;

  return (
    <div className={classes.wrapper}>
      <Card className={classes.listContainer}>
        {tabs ? tabs : null}
        <div className={classes.headerContainer}>
          <Text fontSize={14} lineHeight="21px">
            {displayTitle}
          </Text>
        </div>

        <div className={classes.textFieldContainer}>
          <BaseInput
            fullWidth
            placeholder="Filter"
            value={filterText || ''}
            onChange={(e) => setFilterText(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={
              filterText
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setFilterText(undefined)}>
                          <Cancel />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
        </div>

        <div className={classes.accordionContainer}>{renderTags(filteredTags)}</div>
      </Card>
    </div>
  );
};
